import * as React from "react";
import { replaceAll, useDecimalInputValidation } from "./matrixDataUtils";

interface UseMatrixFormStateOptions<T> {
  /** The external serialized state (JSON string) from the RAFT form field */
  state: string | undefined;
  /** Setter to push serialized state back to the RAFT form field */
  setstate: (value: string) => void;
  /** Parse the JSON string into the form data shape. Should return a default/empty object on failure. */
  parseState: (state: string | undefined) => T;
  /** Serialize form data to JSON string for saving. Defaults to JSON.stringify. */
  serializeState?: (formData: T) => string;
}

interface UseMatrixFormStateResult<T> {
  formData: T;
  setFormData: React.Dispatch<React.SetStateAction<T>>;
  trailingChars: Record<string, string>;
  setTrailingChars: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  hasUserEditedRef: React.MutableRefObject<boolean>;
  validateAndParseInput: (
    inputValue: string,
    currentValue?: number,
    allowNegative?: boolean
  ) => { isValid: boolean; parsedValue?: number; trailingChars: string };
  decimalSeparator: string;
  /** Get the locale-formatted display value for a field, including trailing chars */
  getDisplayValue: (field: string, rawValue: string | number | undefined | null) => string;
  /** Clear trailing chars for a field (call on blur) */
  clearTrailingChars: (field: string) => void;
}

/**
 * Shared form state management hook for matrix parameter forms (Type 1 and Type 2).
 * Handles: JSON parse/sync, trailing decimal chars, locale formatting, user edit tracking.
 */
export function useMatrixFormState<T>({
  state,
  setstate,
  parseState,
  serializeState = (data) => JSON.stringify(data),
}: UseMatrixFormStateOptions<T>): UseMatrixFormStateResult<T> {
  const { validateAndParseInput, decimalSeparator } = useDecimalInputValidation("de-DE");

  const [formData, setFormData] = React.useState<T>(() => parseState(state));
  const [trailingChars, setTrailingChars] = React.useState<Record<string, string>>({});
  const hasUserEditedRef = React.useRef(false);

  // Sync formData from external state changes (e.g., loading saved data on edit)
  React.useEffect(() => {
    if (!hasUserEditedRef.current && state && typeof state === "string" && state.trim() !== "") {
      try {
        const parsed = parseState(state);
        setFormData(parsed);
      } catch {
        // ignore parse errors
      }
    }
    // parseState is a function reference — intentionally excluded from deps to avoid infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  // Save to form state when user makes changes
  React.useEffect(() => {
    if (hasUserEditedRef.current) {
      setstate(serializeState(formData));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, setstate]);

  const getDisplayValue = React.useCallback(
    (field: string, rawValue: string | number | undefined | null): string => {
      if (rawValue === undefined || rawValue === "" || rawValue === null) return "";
      const trailing = trailingChars[field] ?? "";
      const displayValue = `${rawValue.toString()}${trailing}`;
      return replaceAll(displayValue, ".", decimalSeparator);
    },
    [trailingChars, decimalSeparator]
  );

  const clearTrailingChars = React.useCallback((field: string) => {
    setTrailingChars((prev) => ({ ...prev, [field]: "" }));
  }, []);

  return {
    formData,
    setFormData,
    trailingChars,
    setTrailingChars,
    hasUserEditedRef,
    validateAndParseInput,
    decimalSeparator,
    getDisplayValue,
    clearTrailingChars,
  };
}
